/**
 * CustomerNetworkSection — three quiet tabs on every customer profile:
 *   • Family Tree  — entries from `family_members` on the profile
 *   • Friend List  — co-travellers de-duped across all tours led by this
 *                    customer (auto-aggregated, no manual upkeep)
 *   • Referral Tree — recursive descent down pollen_customers.referred_by_id,
 *                    capped at 4 levels deep so the UI stays calm
 *
 * Data comes from `GET /api/pollen/customers/:cid/network` in a single
 * round trip. The page can be rendered with zero family rows and still
 * surface a rich referral story — most of Pollen Dots' "tree" grows out
 * sideways via co-travellers and word-of-mouth, not the immediate family.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Heart, Users, GitBranch, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function CustomerNetworkSection({ customerId, customerName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('family');

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/pollen/customers/${customerId}/network`, {
      credentials: 'include', headers: authHeaders(),
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [customerId]);

  if (loading) return (
    <Card data-testid="customer-network-loading"><CardContent className="py-6 flex items-center gap-2 text-muted-foreground">
      <Loader2 className="w-4 h-4 animate-spin" /> Building network…
    </CardContent></Card>
  );
  if (!data) return null;

  const stats = data.stats || {};
  const tabs = [
    { id: 'family',    label: 'Family',    icon: Heart,     count: stats.family_count    },
    { id: 'friends',   label: 'Friends',   icon: Users,     count: stats.friends_count   },
    { id: 'referrals', label: 'Referrals', icon: GitBranch, count: stats.referrals_count },
  ];

  return (
    <Card data-testid="customer-network">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-700" />
          Network — people connected to {customerName}
        </CardTitle>
        <div className="flex gap-1 mt-2">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} type="button"
                      onClick={() => setTab(t.id)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5
                                  transition-colors
                                  ${tab === t.id
                                    ? 'bg-amber-100 text-amber-900'
                                    : 'text-muted-foreground hover:bg-slate-100'}`}
                      data-testid={`network-tab-${t.id}`}>
                <Icon className="w-3 h-3" />
                {t.label}
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-white/70 text-[10px]">
                  {t.count || 0}
                </span>
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {tab === 'family' && <FamilyList items={data.family || []} />}
        {tab === 'friends' && <FriendsList items={data.friends || []} />}
        {tab === 'referrals' && <ReferralTree items={data.referrals || []} rootName={customerName} totalCount={stats.total_referrals_count} />}
      </CardContent>
    </Card>
  );
}

function EmptyState({ text }) {
  return <p className="text-xs italic text-muted-foreground py-4 text-center">{text}</p>;
}

function PersonRow({ name, sub, badge, phone }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-amber-50 rounded">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{name}</div>
        {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
      </div>
      {phone && <span className="text-[10px] text-muted-foreground">{phone}</span>}
      {badge && <Badge variant="secondary" className="text-[10px] shrink-0">{badge}</Badge>}
    </div>
  );
}

function FamilyList({ items }) {
  if (!items.length) return <EmptyState text="No family members added yet." />;
  return (
    <div className="space-y-1" data-testid="network-family-list">
      {items.map((m, i) => (
        <PersonRow key={i}
          name={m.name || '(Unnamed)'}
          sub={[m.dob && `🎂 ${m.dob.slice(0, 10)}`,
                m.anniversary && `💍 ${m.anniversary.slice(0, 10)}`,
                m.notes].filter(Boolean).join('  ·  ')}
          phone={m.phone}
          badge={m.relation || 'Self'}
        />
      ))}
    </div>
  );
}

function FriendsList({ items }) {
  if (!items.length) return <EmptyState text="No co-travellers recorded across any tour yet." />;
  return (
    <div className="space-y-1" data-testid="network-friends-list">
      {items.map((f, i) => (
        <PersonRow key={i}
          name={f.name}
          sub={[
            f.via_tour && `via ${f.via_tour}`,
            f.via_tour_date && f.via_tour_date.slice(0, 10),
            f.trip_count > 1 && `${f.trip_count} trips together`,
            f.dob && `🎂 ${f.dob.slice(0, 10)}`,
          ].filter(Boolean).join('  ·  ')}
          phone={f.phone}
          badge={f.relation}
        />
      ))}
    </div>
  );
}

function ReferralTree({ items, rootName, totalCount }) {
  if (!items.length) {
    return <EmptyState text="No customers have been referred by this person yet." />;
  }
  // Group by depth so the operator sees the tree shape at a glance.
  const byDepth = items.reduce((acc, r) => {
    (acc[r.depth] = acc[r.depth] || []).push(r);
    return acc;
  }, {});
  const depths = Object.keys(byDepth).sort((a, b) => +a - +b);
  const directCount = (byDepth['1'] || []).length;
  const total = totalCount || items.length;
  return (
    <div className="space-y-3" data-testid="network-referrals-list">
      <div className="text-[10px] text-muted-foreground italic">
        {rootName} → {directCount} direct{directCount === 1 ? '' : 's'}
        {total > directCount ? ` · ${total} total across ${depths.length} hop${depths.length === 1 ? '' : 's'}` : ''}
      </div>
      {depths.map(d => (
        <div key={d}>
          <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-1
                          flex items-center gap-1">
            <GitBranch className="w-3 h-3" />
            Level {d}{d === '1' ? ' — direct referrals' : ` — referred by a level ${+d - 1} contact`}
          </div>
          <div className="space-y-0.5 ml-3">
            {byDepth[d].map(r => (
              <a key={r.id} href={`/pollen-dots/customer/${r.id}`}
                 className="block hover:bg-amber-50 rounded px-2 py-1 transition-colors">
                <PersonRow
                  name={r.name || '(Unnamed)'}
                  sub={[
                    r.joined && `joined ${r.joined}`,
                    r.dob && `🎂 ${r.dob.slice(0, 10)}`,
                    r.source === 'promoted_from_family' && 'was a family member',
                  ].filter(Boolean).join('  ·  ')}
                  phone={r.phone}
                />
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
